import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, InputBase, Stack, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { parseNameList } from '../../utils/nameList';
import { useSequentialCreate } from '../../hooks/useSequentialCreate';

export interface QuickAddPreset {
  label: string;
  names: string[];
}

interface QuickAddProps {
  /** e.g. { one: 'class', many: 'classes' } — used in the button and messages. */
  noun: { one: string; many: string };
  placeholder: string;
  /** Names already in the list; matches are shown and skipped. */
  existing: string[];
  /** Creates one item. Items are created in order, one at a time. */
  create: (name: string) => Promise<unknown>;
  /** Called once after a run that created anything, to reload the list. */
  onAdded: () => void;
  /** Expand numeric ranges such as "Class 1-10". */
  ranges?: boolean;
  /** One-click starting lists, offered while the box is empty. */
  presets?: QuickAddPreset[];
  disabled?: boolean;
}

const plural = (n: number, noun: { one: string; many: string }) => `${n} ${n === 1 ? noun.one : noun.many}`;

/**
 * Add one item or many: type or paste names separated by commas or new
 * lines, review them as chips, and add them all at once.
 */
const QuickAdd: React.FC<QuickAddProps> = ({ noun, placeholder, existing, create, onAdded, ranges = false, presets, disabled }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [text, setText] = useState('');
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const { progress, busy, run } = useSequentialCreate();
  const [failures, setFailures] = useState<Array<{ name: string; message: string }>>([]);

  const taken = useMemo(() => new Set(existing.map(n => n.trim().toLowerCase())), [existing]);
  const parsed = useMemo(() => parseNameList(text, { ranges }).filter(n => !removed.has(n)), [text, ranges, removed]);
  const fresh = parsed.filter(n => !taken.has(n.toLowerCase()));

  const reset = () => { setText(''); setRemoved(new Set()); };

  const add = async () => {
    if (!fresh.length || busy) return;
    setFailures([]);
    const { added, failed } = await run(fresh, create);
    if (added > 0) {
      const one = fresh.length === 1 ? `${fresh[0]} added` : `${plural(added, noun)} added`;
      enqueueSnackbar(one, { variant: 'success' });
      onAdded();
    }
    setFailures(failed.map(f => ({ name: f.item, message: f.message })));
    // Keep only what failed in the box, so it can be fixed and retried.
    setRemoved(new Set());
    setText(failed.map(f => f.item).join('\n'));
  };

  const label = busy
    ? `Adding ${progress!.done + 1} of ${progress!.total}…`
    : fresh.length > 1 ? `Add ${plural(fresh.length, noun)}` : `Add ${noun.one}`;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: '12px',
        bgcolor: alpha(theme.palette.text.primary, 0.015),
        transition: theme.transitions.create(['border-color', 'box-shadow'], { duration: 150 }),
        '&:focus-within': {
          borderColor: 'primary.main',
          boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.12)}`,
        },
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ px: 1.5, py: 1 }}>
        <Add sx={{ mt: 0.75, fontSize: 20, color: 'text.disabled' }} />
        <InputBase
          value={text}
          onChange={e => { setText(e.target.value); setFailures([]); }}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); add(); }
            if (e.key === 'Escape') reset();
          }}
          placeholder={placeholder}
          multiline
          maxRows={6}
          fullWidth
          disabled={disabled || busy}
          inputProps={{ 'aria-label': `Add ${noun.many}` }}
          sx={{ fontSize: '0.875rem', py: 0.5 }}
        />
        <Button
          variant="contained"
          disableElevation
          size="small"
          onClick={add}
          disabled={disabled || busy || fresh.length === 0}
          sx={{ flexShrink: 0, whiteSpace: 'nowrap', minHeight: 32 }}
        >
          {label}
        </Button>
      </Stack>

      {(parsed.length > 1 || parsed.some(n => taken.has(n.toLowerCase()))) && (
        <Box sx={{ px: 1.5, pb: 1.25, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {parsed.map(name => {
            const dup = taken.has(name.toLowerCase());
            const chip = (
              <Chip
                key={name}
                label={name}
                size="small"
                variant="outlined"
                onDelete={busy ? undefined : () => setRemoved(prev => new Set(prev).add(name))}
                sx={{
                  borderRadius: '7px',
                  fontWeight: 500,
                  ...(dup && { opacity: 0.5, textDecoration: 'line-through' }),
                }}
              />
            );
            return dup ? <Tooltip key={name} title="Already in the list — it will be skipped">{chip}</Tooltip> : chip;
          })}
        </Box>
      )}

      {!text && presets && presets.length > 0 && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, pb: 1.25, flexWrap: 'wrap', rowGap: 0.75 }}>
          <Typography variant="caption" color="text.secondary">Start from</Typography>
          {presets.map(p => (
            <Chip
              key={p.label}
              label={p.label}
              size="small"
              clickable
              onClick={() => setText(p.names.join('\n'))}
              sx={{ borderRadius: '7px', fontWeight: 500 }}
            />
          ))}
        </Stack>
      )}

      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', px: 1.5, pb: 1, mt: -0.25 }}>
        Add several at once — separate them with commas or new lines{ranges ? ', or type a range like "Class 1-10"' : ''}. Enter to add, Shift+Enter for a new line.
      </Typography>

      {failures.length > 0 && (
        <Alert severity="error" sx={{ m: 1.5, mt: 0 }}>
          {failures.length === 1
            ? `"${failures[0].name}" was not added: ${failures[0].message}`
            : `${plural(failures.length, noun)} were not added. They are back in the box to fix and retry.`}
          {failures.length > 1 && (
            <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
              {failures.map(f => <li key={f.name}>{f.name}: {f.message}</li>)}
            </Box>
          )}
        </Alert>
      )}
    </Box>
  );
};

export default QuickAdd;
